import { cn } from "~/lib/utils";

/**
 * Borde de papel RASGADO para las costuras entre secciones.
 *
 * Por qué no un degradado: mezclar crema con casi-negro obliga a pasar por medios tonos
 * que ensucian, y encima necesita una banda muerta larga que le pasa por encima al
 * contenido. Acá la hoja de arriba simplemente se ROMPE y abajo aparece la otra sección.
 *
 * El trazo es IRREGULAR a propósito (un rasgado real no es un festón que se repite):
 * son 24 tramos con alturas dispares. `preserveAspectRatio="none"` lo estira a cualquier
 * ancho sin recortar. `flip` lo da vuelta para usarlo como cierre inferior.
 */

/** mulberry32: PRNG estable (mismo trazo en SSR y cliente) y sin perder precisión. */
function makeRnd(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Perfil del rasgado, de derecha a izquierda. Se genera una sola vez al cargar el módulo.
 * Clave: el ANCHO de cada diente también es irregular (8–34) — con pasos parejos de 50 el
 * borde leía como sierra de montaña, no como papel roto. Con ~55 dientes de ancho dispar
 * y poca amplitud, lee como fibra rasgada.
 */
const TEAR = (() => {
  const rnd = makeRnd(20260725);
  const parts: string[] = [];
  let x = 1200;
  while (x > 0) {
    x = Math.max(0, x - (8 + rnd() * 26));
    parts.push(`L${x.toFixed(1)},${(11 + rnd() * 23).toFixed(1)}`);
  }
  return parts.join(" ");
})();

export function TornEdge({
  color,
  className,
  flip = false,
}: {
  /** Color de la hoja que se rasga (la sección de la que viene el papel). */
  color: string;
  className?: string;
  /** true = el rasgado apunta hacia arriba (para cerrar una sección por abajo). */
  flip?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 1200 48"
      preserveAspectRatio="none"
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-x-0 z-[1] block h-[34px] w-full sm:h-[46px]",
        className,
      )}
      style={flip ? { transform: "scaleY(-1)" } : undefined}
    >
      {/* La sombra dura le da al papel el canto levantado. En modo CLARO cae sobre la banda
          oscura y se lee como relieve; en OSCURO dibuja una línea negra en sierra sobre un
          fondo que ya es casi negro, y lo único que se ve es suciedad. Medido: tres niveles
          por debajo de la banda, justo en el borde. Por eso se apaga con el tema. */}
      <path
        d={`M0,0 H1200 V20 ${TEAR} Z`}
        fill={color}
        className="[filter:drop-shadow(0_1.5px_0_rgba(0,0,0,0.13))] dark:[filter:none]"
      />
    </svg>
  );
}
